import { Audiobook, AudiobookPerson, AudiobookTopic } from './audiobooks';
import { API_BASE_URL } from './config';
import { authHeaders } from './auth';

export type PreferencesData = {
  topic_ids: number[];
  person_ids: number[];
  topics: AudiobookTopic[];
  people: AudiobookPerson[];
};

export type LibraryState = {
  audiobook_id: number;
  is_favorite: boolean;
  viewed_at?: string | null;
  last_listened_at?: string | null;
  last_position: number;
  duration: number;
  chapter_id?: number | null;
};

export type FavoritePersonState = {
  person_id: number;
  is_favorite: boolean;
};

type ApiEnvelope<T> = {
  data?: T;
  message?: string;
  error?: boolean;
};

async function request<T>(path: string, options: RequestInit = {}) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...((options.headers as Record<string, string> | undefined) ?? {}),
    },
  });
  const json = await response.json().catch(() => ({})) as ApiEnvelope<T> | T;

  if (!response.ok || ('error' in Object(json) && (json as ApiEnvelope<T>).error)) {
    throw new Error((json as ApiEnvelope<T>).message || `API ${response.status}: ${path}`);
  }

  return 'data' in Object(json) ? (json as ApiEnvelope<T>).data as T : json as T;
}

function normalizeList<T>(value: T[] | { data?: T[] }) {
  if (Array.isArray(value)) return value;
  return Array.isArray(value.data) ? value.data : [];
}

export function getPreferences() {
  return request<PreferencesData>('/audiobook-mobile/me/preferences');
}

export function savePreferences(topicIds: number[], personIds: number[]) {
  return request<PreferencesData>('/audiobook-mobile/me/preferences', {
    method: 'PUT',
    body: JSON.stringify({
      topic_ids: topicIds,
      person_ids: personIds,
    }),
  });
}

export function getLibrary(type: 'all' | 'favorites' | 'viewed' | 'listening' = 'all') {
  return request<Audiobook[] | { data?: Audiobook[] }>(`/audiobook-mobile/me/library?type=${type}`).then(normalizeList);
}

export function getLibraryState(id: number) {
  return request<LibraryState>(`/audiobook-mobile/me/library/${id}/state`);
}

export function toggleFavorite(id: number, favorite?: boolean) {
  return request<LibraryState>(`/audiobook-mobile/me/library/${id}/favorite`, {
    method: 'POST',
    body: JSON.stringify(favorite === undefined ? {} : { favorite }),
  });
}

export function getFavoritePeople() {
  return request<AudiobookPerson[] | { data?: AudiobookPerson[] }>('/audiobook-mobile/me/favorite-people').then(normalizeList);
}

export function getFavoritePersonState(id: number) {
  return request<FavoritePersonState>(`/audiobook-mobile/me/favorite-people/${id}/state`);
}

export function toggleFavoritePerson(id: number, favorite?: boolean) {
  return request<FavoritePersonState>(`/audiobook-mobile/me/favorite-people/${id}/favorite`, {
    method: 'POST',
    body: JSON.stringify(favorite === undefined ? {} : { favorite }),
  });
}

export function markViewed(id: number) {
  return request<LibraryState>(`/audiobook-mobile/me/library/${id}/viewed`, {
    method: 'POST',
  });
}

export function saveProgress(id: number, position: number, duration: number, chapterId?: number | null) {
  return request<LibraryState>(`/audiobook-mobile/me/library/${id}/progress`, {
    method: 'POST',
    body: JSON.stringify({
      position: Math.max(0, Math.round(position || 0)),
      duration: Math.max(0, Math.round(duration || 0)),
      chapter_id: chapterId || undefined,
    }),
  });
}
